import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';
import { coverScale } from '../artwork-texture.js';
import { getMoonlake } from '../moonlake-assets.js';

// Moonlake - a mountain lake at night. Over the track the moon rises slowly
// out of the notch between the mountains and lays a path of glittering light
// on the water. Ambient, solo piano, meditation, classical. Rules of
// kiosk-app/CLAUDE.md (R1-R11); danceability 0.10 = R9's calm band: no
// visible pulse at all.
//
// ONE QUAD. The photo (tools/build-moonlake.mjs: monlake.jpeg, its lossless
// masks R sky / G mountains / B water, the measured water line, notch and
// skyline) is shown cover-fit and left untouched (R3's photo exception, R4);
// on top, all procedural: the moon, its halo, the brightened sky, the lit
// mist on the water line and the moon path. The safe-zone floor 0.6
// multiplies the procedural layers only, never the photo.
//
// THE MOON rises from behind the mountains: its disc is multiplied by the
// SKY mask, so the ridge hides it while it climbs (its centre starts one
// radius under the skyline). Its course is set in JS from the manifest: the
// notch's x, from under the skyline to RISE above it, over progress 0..0.7,
// then it stays. If that course crosses the DOM cover's zone (the notch sits
// right behind it on a 16:9 screen), the rising point moves beside the cover
// to the nearer side, where the skyline is read again: the moon is never
// hidden by the cover (criterion 4). Disc: low-frequency "seas" (value
// noise), limb darkening, amber near the horizon to white higher up.
//
// THE MOON PATH - the decisive detail. On the water under the moon, a column
// of thousands of small glints: the water is cut into cells in SCREEN pixels
// (GLINT_PX wide, flattened like ripples), each cell holds at most one glint
// at a random spot; a cell is lit when its random draw is under the column's
// density (densest on the axis), and each glint twinkles on its own phase.
// The column follows the moon's x and lengthens as it climbs; outside it the
// photo's reflection of the mountains is untouched.
//
// THE MUSIC, no pulse (R9 < 0.30): the twinkle follows bands.high smoothed
// over 2 bars, the path's width the energy smoothed over 8 bars.

const RIG = 'forbidden';
export { RIG };

export const PROFILE = {
  id: 'moonlake',
  family: 'photographic',
  danceability: 0.10,
  requiresTier: 0,
  energyLevel: 0.06,
  bpmMin: 40,
  bpmMax: 100,
  movement: 0.05,
  tonality: 0.95,
  enabled: true
};

const TIER = {
  0: { glintDensity: 0.5 },
  1: { glintDensity: 1.0 },
  2: { glintDensity: 1.0 }
};
const DEFAULT_TIER = 2;

// --- the moon (screen height units: the frame is 2 tall) ---------------------
const MOON_R = 0.05;
const RISE = 0.24;                // how far above the skyline it ends (screen units)
const RISE_END = 0.70;            // of the progress
const TOP_ROOM = 0.10 + MOON_R;   // never closer to the top than this (5 % margin + radius)
const SAFE_GAP = 1.6;             // x the radius: room kept between the moon and the cover's zone
const AMBER = [1.0, 0.74, 0.48], WHITE = [0.92, 0.95, 1.0];
const MOON_LUM = 0.82;            // at 0.95 the bloom and the tone map's shoulder flattened the seas
const HALO = 0.15;
const SKY_LIFT = 0.045;
const MIST = 0.16;
// --- the path --------------------------------------------------------------------
const GLINT_PX = [4.0, 1.6];      // a glint cell, px (wide and flat like ripples)
const PATH_LEN = 2.4;             // x the moon's height above the water line
const PATH_W0 = 1.1;              // x the radius at the water line
const PATH_SPREAD = 1.6;          // widening per screen unit toward the viewer
const GLINT_MAX = 1.05;           // the brightest glints just bloom
const SAFE_FLOOR = 0.6;
const PROGRESS_TAU = 2.0;         // s: the progress is smoothed (never a jump)
const RADIO_LOOP_SECONDS = 600;
const FREE_BPM = 70;

function urlParam(name) {
  if (typeof window === 'undefined' || !window.location) return null;
  try { return new URLSearchParams(window.location.search).get(name); } catch (e) { return null; }
}
function clamp01(x) { return Math.max(0, Math.min(1, x)); }
function smooth01(x) { const t = clamp01(x); return t * t * (3 - 2 * t); }

const quadVertex = `
  varying vec2 vUv;
  void main() { vUv = uv; gl_Position = vec4(position.xy, 0.9999, 1.0); }
`;

const fragment = `
  precision highp float;   // the glint cells are indexed by screen pixel (fp16 on the TV runs out at 2048)
  uniform sampler2D uPhoto;
  uniform sampler2D uMask;
  uniform vec2 uFit;
  uniform vec2 uMoon;            // centre, screen height units
  uniform float uMoonR;
  uniform float uMoonA;          // 0 before it rises (and while hidden at the start)
  uniform vec3 uMoonCol;
  uniform float uWater;          // the water line, screen height units
  uniform float uPathW;          // the energy's width factor
  uniform float uDensity;        // glints: the tier's density
  uniform float uTwinkle;        // bands.high, smoothed: rate and depth of the twinkle
  uniform float uTime;
  uniform float uPxUnit;         // px per screen height unit
  uniform float uDebugMask;
  uniform float uAspect;
  uniform vec2 uSafeCenter;
  uniform vec2 uSafeHalfSize;
  uniform float uSafeFeather;
  varying vec2 vUv;

  float safeAtt(vec2 s) {
    vec2 q = abs(s - uSafeCenter) - uSafeHalfSize;
    float d = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0);
    return mix(${SAFE_FLOOR.toFixed(2)}, 1.0, smoothstep(0.0, uSafeFeather, d));
  }
  float hash(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
  float vnoise(vec2 p) {
    vec2 i = floor(p), f = fract(p);
    f = f * f * (3.0 - 2.0 * f);
    return mix(mix(hash(i), hash(i + vec2(1.0, 0.0)), f.x), mix(hash(i + vec2(0.0, 1.0)), hash(i + vec2(1.0, 1.0)), f.x), f.y);
  }

  void main() {
    vec2 puv = (vUv - 0.5) * uFit + 0.5;
    vec3 photo = texture2D(uPhoto, puv).rgb;
    vec3 mask = texture2D(uMask, puv).rgb;          // r sky, g mountains, b water
    if (uDebugMask > 0.5) { gl_FragColor = vec4(photo * 0.4 + mask * 0.6, 1.0); return; }
    vec2 s = (vUv * 2.0 - 1.0) * vec2(uAspect, 1.0);
    vec2 rel = s - uMoon;
    float d = length(rel);
    vec3 add = vec3(0.0);
    // --- the moon, behind the mountains: only where the sky is ---
    if (uMoonA > 0.0) {
      if (d < uMoonR * 1.02) {
        vec2 q = rel / uMoonR;
        float r2 = dot(q, q);
        float seas = vnoise(q * 2.2 + 3.1) * 0.6 + vnoise(q * 4.7 + 11.0) * 0.4;
        float shade = (0.74 + 0.26 * sqrt(max(0.0, 1.0 - r2))) * (1.0 - 0.42 * smoothstep(0.42, 0.72, seas));
        float edge = 1.0 - smoothstep(uMoonR - 1.0 / uPxUnit, uMoonR + 1.0 / uPxUnit, d);
        add += uMoonCol * ${MOON_LUM.toFixed(2)} * shade * edge * mask.r;
      }
      // the halo, wide and soft, under the bloom threshold; faint over the mountains
      float halo = exp(-d / (uMoonR * 2.6)) * ${HALO.toFixed(2)} + exp(-d / (uMoonR * 9.0)) * 0.06;
      float outside = smoothstep(uMoonR * 0.9, uMoonR * 1.2, d);
      add += uMoonCol * halo * outside * (mask.r + 0.03 * mask.g);   // 0.12 greyed the ridge in front of the rising moon
      // the sky around it lifts a little
      add += uMoonCol * ${SKY_LIFT.toFixed(2)} * exp(-d / 0.45) * mask.r;
      // the lake's mist, lit under the moon, on the water line
      float dx = (s.x - uMoon.x);
      float mist = exp(-dx * dx / 0.05) * exp(-abs(s.y - uWater) / 0.018);
      add += uMoonCol * ${MIST.toFixed(2)} * mist * (mask.b + mask.g * 0.5);
      // --- the moon path: a column of glints under the moon ---
      float down = uWater - s.y;                    // distance below the water line
      if (down > 0.0 && mask.b > 0.01) {
        float len = max(uMoon.y - uWater, 0.02) * ${PATH_LEN.toFixed(2)};
        float w = uMoonR * ${PATH_W0.toFixed(2)} * (1.0 + ${PATH_SPREAD.toFixed(2)} * down) * uPathW;
        float col = exp(-dx * dx / (w * w));
        float along = 1.0 - smoothstep(len * 0.55, len, down);
        float dens = col * along;
        // a faint sheen under the glints, to hold the column together
        add += uMoonCol * 0.045 * dens * mask.b;
        // glints: cells in screen pixels, gently rippling
        vec2 pp = s * uPxUnit;
        pp.x += 3.0 * sin(pp.y * 0.07 + uTime * 0.35);
        vec2 cellSize = vec2(${GLINT_PX[0].toFixed(1)}, ${GLINT_PX[1].toFixed(1)});
        vec2 cell = floor(pp / cellSize);
        float h1 = hash(cell), h2 = hash(cell + 17.31), h3 = hash(cell + 41.7);
        if (h1 < dens * 0.55 * uDensity) {
          vec2 c = (cell + 0.25 + 0.5 * vec2(h2, h3)) * cellSize;
          vec2 o = (pp - c) / vec2(1.6, 0.7);
          float shape = exp(-dot(o, o));
          float rate = (0.5 + 0.9 * uTwinkle) * (0.6 + 0.8 * h2);
          float tw = pow(max(0.0, sin(uTime * rate * 3.0 + h3 * 6.283)), 6.0 - 3.0 * uTwinkle);
          add += uMoonCol * ${GLINT_MAX.toFixed(2)} * shape * tw * (0.35 + 0.65 * col) * mask.b;
        }
      }
      add *= uMoonA;
    }
    // the safe zone multiplies the procedural layers only, never the photo
    gl_FragColor = vec4(photo + add * safeAtt(s), 1.0);
  }
`;

// Compiled once and never released (pi-shader-compile-cost); uniforms live with it.
let MATERIAL = null;
function material() {
  if (MATERIAL) return MATERIAL;
  const u = {
    uPhoto: { value: null }, uMask: { value: null }, uFit: { value: new THREE.Vector2(1, 1) },
    uMoon: { value: new THREE.Vector2(0, -2) }, uMoonR: { value: MOON_R }, uMoonA: { value: 0 },
    uMoonCol: { value: new THREE.Color(1, 1, 1) }, uWater: { value: 0 }, uPathW: { value: 1 },
    uDensity: { value: 1 }, uTwinkle: { value: 0.5 }, uTime: { value: 0 }, uPxUnit: { value: 360 }, uDebugMask: { value: 0 },
    uAspect: safeZoneUniforms.uAspect, uSafeCenter: safeZoneUniforms.uSafeCenter,
    uSafeHalfSize: safeZoneUniforms.uSafeHalfSize, uSafeFeather: safeZoneUniforms.uSafeFeather
  };
  MATERIAL = { material: new THREE.ShaderMaterial({ uniforms: u, vertexShader: quadVertex, fragmentShader: fragment, depthTest: false, depthWrite: false }), u };
  return MATERIAL;
}

const _size = new THREE.Vector2();

export class MoonlakeVisualizer extends Visualizer {
  constructor(options) {
    super();
    const tier = options && options.qualityTier != null ? options.qualityTier : DEFAULT_TIER;
    this._tier = TIER[tier] ? tier : DEFAULT_TIER;
    this._layout = TIER[this._tier];
    const mp = parseFloat(urlParam('moonp'));
    this._forcedP = Number.isFinite(mp) ? clamp01(mp) : null;
    this._debugMask = urlParam('moonmask') === '1';
  }

  // calm: a soft bloom, only the moon and the brightest glints reach it
  get postProcessing() { return { bloomStrength: 0.25, bloomThreshold: 0.78 }; }   // 0.45 spilled the moon over the ridge it hides behind

  init(scene, camera) {
    this._scene = scene;
    this._assets = getMoonlake();
    const m = material();
    this._u = m.u;
    this._quad = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), m.material);
    this._quad.frustumCulled = false;
    this._quad.renderOrder = -1000;
    const self = this;
    this._quad.onBeforeRender = function (renderer) { self._layout_(renderer); };
    // Without the photo or the masks, Moonlake is unavailable (the entry
    // points withdraw it); if it is forced anyway, the quad stays hidden:
    // the frame is black, not broken.
    this._quad.visible = !!this._assets;
    scene.add(this._quad);
    const u = this._u;
    if (this._assets) {
      u.uPhoto.value = this._assets.photo; u.uMask.value = this._assets.mask;
    }
    u.uDensity.value = this._layout.glintDensity;
    u.uDebugMask.value = this._debugMask ? 1 : 0;
    u.uMoonA.value = 0; u.uTime.value = 0;
    this._time = 0;
    this._progress = null;
    this._trackChangedAt = Date.now();
    this._lastTrack = undefined;
    this._high = 0.4; this._energy = 0.4;
    this._beatSeconds = 60 / FREE_BPM;
    this._aspect = 0;
    this._course = null;
  }

  // --- the moon's course, from the manifest, for this aspect and safe zone ---------------

  // screen height units <-> image fractions (x right, y DOWN), through the cover-fit
  _toScreen(ix, iy, out) {
    const f = this._u.uFit.value, a = this._aspect;
    const vx = (ix - 0.5) / f.x + 0.5, vy = ((1 - iy) - 0.5) / f.y + 0.5;
    out[0] = (vx * 2 - 1) * a; out[1] = vy * 2 - 1;
    return out;
  }
  _toImageX(sx) { const f = this._u.uFit.value; return ((sx / this._aspect + 1) / 2 - 0.5) * f.x + 0.5; }
  _skylineAt(ix) {
    const sk = this._assets.manifest.skyline;
    const k = clamp01(ix) * (sk.length - 1), i = Math.floor(k), t = k - i;
    const a = sk[i], b = sk[Math.min(sk.length - 1, i + 1)];
    return a + (b - a) * t;
  }

  // The course: x (screen) and the y of the start (hidden: centre one radius
  // under the skyline) and of the end (RISE above it, kept TOP_ROOM from the
  // top). If it crosses the cover's zone, x moves beside it, nearer side
  // first, out of the top UI zones.
  _plan() {
    const man = this._assets.manifest, a = this._aspect;
    // R19: the course keeps clear of the stage block (screen centre); the
    // cover only says whether there is one at all.
    const sh = safeZoneUniforms.uSafeHalfSize.value;
    const present = sh.x > 0 && sh.y > 0;
    const c = safeZoneUniforms.uStageCenter.value, hs = safeZoneUniforms.uStageHalfSize.value;
    const pt = [0, 0];
    const course = (sx) => {
      const ix = this._toImageX(sx);
      const sky = this._toScreen(ix, this._skylineAt(ix), pt)[1];
      return { x: sx, y0: sky - MOON_R * 1.05, y1: Math.min(sky + RISE, 1 - TOP_ROOM) };
    };
    const crosses = (k) => {
      if (!present) return false;
      const g = MOON_R * SAFE_GAP;
      return Math.abs(k.x - c.x) < hs.x + g && k.y1 > c.y - hs.y - g && k.y0 < c.y + hs.y + g;
    };
    const inUi = (k) => {
      const nx = k.x / a, ny = k.y1;                   // NDC of the final centre
      const rx = MOON_R / a, ry = MOON_R;
      return (nx + rx > 1 - 0.44 && ny + ry > 1 - 0.24) || (nx - rx < -1 + 0.16 && ny + ry > 1 - 0.24) || Math.abs(nx) > 1 - 0.1 / a - rx;
    };
    const notchX = this._toScreen(man.notch[0], man.notch[1], pt)[0];
    let k = course(notchX), shifted = false;
    if (crosses(k)) {
      const g = MOON_R * SAFE_GAP;
      const sides = [c.x + hs.x + g, c.x - hs.x - g].sort((p, q) => Math.abs(p - notchX) - Math.abs(q - notchX));
      shifted = true;
      k = null;
      for (const sx of sides) { const t = course(sx); if (!inUi(t) && !crosses(t)) { k = t; break; } }
      if (!k) k = course(sides[0]);
    }
    this._course = Object.assign(k, { shifted, notchX });
  }

  _layout_(renderer) {
    if (!this._assets) return;
    renderer.getDrawingBufferSize(_size);
    const aspect = _size.x / Math.max(1, _size.y);
    this._u.uPxUnit.value = _size.y / 2;
    const c = safeZoneUniforms.uStageCenter.value, hs = safeZoneUniforms.uStageHalfSize.value;   // R19
    const sh = safeZoneUniforms.uSafeHalfSize.value;
    const key = aspect.toFixed(4) + c.x.toFixed(3) + c.y.toFixed(3) + hs.x.toFixed(3) + hs.y.toFixed(3) + (sh.x > 0 && sh.y > 0 ? 'p' : 'a');
    if (key !== this._planKey) {
      this._aspect = aspect;
      const sz = this._assets.manifest.size;
      this._u.uFit.value.copy(coverScale(aspect, sz[0] / sz[1]));
      const pt = [0, 0];
      this._u.uWater.value = this._toScreen(0.5, this._assets.manifest.waterLine, pt)[1];
      this._plan();
      this._planKey = key;
    }
    this._writeMoon();
  }

  _writeMoon() {
    const u = this._u, k = this._course;
    if (!k) return;
    const p = this._progress == null ? 0 : this._progress;
    const e = clamp01(p / RISE_END);                   // linear: the slowest constant speed
    const y = k.y0 + (k.y1 - k.y0) * e;
    u.uMoon.value.set(k.x, y);
    u.uMoonR.value = MOON_R;
    // the moon is drawn from the first instant; the ridge (sky mask) hides it
    u.uMoonA.value = 1;
    // amber near the horizon, white higher up
    const t = smooth01(e / 0.6);
    u.uMoonCol.value.setRGB(AMBER[0] + (WHITE[0] - AMBER[0]) * t, AMBER[1] + (WHITE[1] - AMBER[1]) * t, AMBER[2] + (WHITE[2] - AMBER[2]) * t);
    this._e = e;
  }

  // --- the frame -------------------------------------------------------------------

  update(deltaTime, profile) {
    // Pause: frozen, never black (R11) - nothing advances, the frame stays.
    if (profile.playbackState !== 'PLAYING') return;
    const dt = Math.min(deltaTime, 0.05);
    this._time += dt;
    if (profile.trackId !== this._lastTrack) { this._lastTrack = profile.trackId; this._progress = null; this._trackChangedAt = Date.now(); }
    // --- progress: the moon's clock ---
    let target;
    if (this._forcedP != null) target = this._forcedP;
    else if (profile.duration > 0) target = clamp01(profile.progress || 0);
    else target = ((Date.now() - (profile.trackChangedAt || this._trackChangedAt)) / 1000 / RADIO_LOOP_SECONDS) % 1;
    if (this._progress == null || Math.abs(target - this._progress) > 0.5) this._progress = target;
    else this._progress += (target - this._progress) * (1 - Math.exp(-dt / PROGRESS_TAU));

    // --- the music: smoothed, no pulse ---
    const bpmRaw = profile.trackFeatures && profile.trackFeatures.bpm;
    this._beatSeconds = 60 / (bpmRaw > 0 ? THREE.MathUtils.clamp(bpmRaw, 40, 200) : FREE_BPM);
    const director = profile.director || {};
    const bands = director.hasReliableGrid ? director.bands : null;
    const high = bands ? clamp01(bands.high || 0) : 0.4;
    const energy = bands ? clamp01(((bands.low || 0) + (bands.mid || 0) + (bands.high || 0)) / 3) : 0.4;
    this._high += (high - this._high) * (1 - Math.exp(-dt / (8 * this._beatSeconds)));     // 2 bars
    this._energy += (energy - this._energy) * (1 - Math.exp(-dt / (32 * this._beatSeconds)));  // 8 bars
    const u = this._u;
    u.uTwinkle.value = this._high;
    u.uPathW.value = 0.75 + 0.6 * this._energy;
    u.uTime.value = this._time;
    if (this._course) this._writeMoon();
  }

  get probe() {
    const u = this._u;
    return {
      ready: !!this._assets, progress: this._progress, e: this._e, moon: u.uMoon.value.toArray(), moonR: u.uMoonR.value,
      water: u.uWater.value, course: this._course, fit: u.uFit.value.toArray(), twinkle: this._high, energy: this._energy,
      pathW: u.uPathW.value, density: u.uDensity.value
    };
  }

  dispose() {
    this._quad.onBeforeRender = null;
    this._scene.remove(this._quad);
    this._quad.geometry.dispose();
    this._u.uPhoto.value = null; this._u.uMask.value = null;
    this._scene = null;
  }
}
